import type { ReactNode } from 'react';
import { useCallback } from 'react';
import PNV from '@components/PositiveNegativeValue';
import { CellLink } from '../DataTable';
import { CellButtonLink, PE, Name } from './styled';
import { useTracking } from 'react-tracking';
import If from '@components/If';
import { Bones } from '@simplywallst/ui-core';
import { useMarketIndustryUrlGetter } from '@/pages/Market/hooks/useMarketIndustryUrlGetter';
import { useReduxForDashboard } from '../../hooks/useReduxForDashboard';

export interface Props {
  id: string;
  name: string;
  slug?: string;
  return7D: number | null;
  return7DFormatted: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  PERatio: number | null;
  PERatioFormatted: string;
  index: number;
}

const Click = ({
  id,
  slug,
  name,
  index,
  children,
}: Pick<Props, 'id' | 'name' | 'slug' | 'index'> & { children: ReactNode }) => {
  const canonicalUrl = useMarketIndustryUrlGetter(id);
  const { navigateToScreener } = useReduxForDashboard();
  const { trackEvent } = useTracking();
  const handleTracking = useCallback(() => {
    const [market, industry] = JSON.parse(id);
    trackEvent({
      action: 'click',
      modifier: 'market_industry_list_item',
      industryId: industry,
      market: market,
      name,
      index,
    });
  }, [id, index, name, trackEvent]);
  const callback = useCallback(() => {
    const [market] = JSON.parse(id);
    navigateToScreener(market, slug);
    handleTracking();
  }, [handleTracking, id, slug, navigateToScreener]);
  return typeof canonicalUrl === 'undefined' ? (
    <CellButtonLink onClick={callback}>{children}</CellButtonLink>
  ) : (
    <CellLink to={canonicalUrl} onClick={handleTracking} noUnderline>
      {children}
    </CellLink>
  );
};

const MyMarketsItem = ({
  id,
  slug,
  name,
  return7D,
  return7DFormatted,
  return1YrAbs,
  return1YrAbsFormatted,
  PERatio,
  PERatioFormatted,
  index,
}: Props) => (
  <tr
    key={`my-markets-item-${name}-${id}`}
    data-cy-id="dashboard-top-worst-my-markets-item"
  >
    <Name>
      <Click id={id} slug={slug} name={name} index={index}>
        <If match={name.length > 0}>
          <span>{name}</span>
        </If>
        <If match={name.length === 0}>
          <Bones />
        </If>
      </Click>
    </Name>
    <PNV as="td" value={return7D}>
      <Click id={id} slug={slug} name={name} index={index}>
        {return7DFormatted}
      </Click>
    </PNV>
    <PNV as="td" value={return1YrAbs}>
      <Click id={id} slug={slug} name={name} index={index}>
        {return1YrAbsFormatted}
      </Click>
    </PNV>
    <PE value={PERatio}>
      <Click id={id} slug={slug} name={name} index={index}>
        {PERatioFormatted}
      </Click>
    </PE>
  </tr>
);

export default MyMarketsItem;
